import {
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  InlineStack,
  Text,
} from "@shopify/polaris";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import { dismissAlert } from "../api/alerts";
import { canonicalStockyPublicBigIntId } from "../api/stocky_public_ids";
import type { Alert } from "../types";

import { t } from "../lib/i18n";

interface Props {
  alerts: Alert[];
}

/**
 * アラート1件の行き先＝発注。仕入先が分かっていればその仕入先の注文書を開いた
 * 状態まで運ぶ（`?supplier=`）。割当が無い商品は発注書に載らないので、先に
 * 割り当てさせる。
 *
 * 🔴 ここが「対応済みにする」しか無かった間、在庫低下を知らせておきながら
 * 次の操作を一切案内していなかった＝マーチャントは仕入先を自分で思い出して
 * 注文書から探し直していた（空状態に行き先を添える方針の、非空状態版）。
 */
function reorderDestination(alert: Alert): { label: string; path: string } {
  if (alert.supplier_id !== null && alert.supplier_id !== undefined) {
    const supplierId = canonicalStockyPublicBigIntId(alert.supplier_id);
    return {
      label: t("{name} に発注する", { name: alert.supplier_name ?? t("この仕入先") }),
      path: `/purchase-orders?supplier=${supplierId}`,
    };
  }
  return { label: t("仕入先を割り当てる"), path: "/purchase-orders" };
}

// 🔴 ここで t() を呼ばない＝モジュール直下の const は initLocale() より前に
//    評価されるので locale が効かない。包むのは使う側（t(KIND_LABEL[kind])）。
const KIND_LABEL: Record<Alert["kind"], string> = {
  low_stock: "在庫低下",
  out_of_stock: "在庫切れ",
};

export function AlertsList({ alerts }: Props) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const mutation = useMutation({
    mutationFn: (id: Alert["id"]) => dismissAlert(id),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["alerts"] });
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });

  if (alerts.length === 0) {
    return (
      <Card>
        <Text as="p" variant="bodyMd" tone="subdued">
          {t("現在、未対応のアラートはありません。")}
        </Text>
      </Card>
    );
  }

  return (
    <Card>
      <BlockStack gap="300">
        {mutation.isError && (
          <Banner tone="critical" title={t("対応済みへの更新結果を確認できませんでした")}>
            <Text as="p">
              {t("通信状況によっては更新済みの可能性があります。最新の状態を再取得しています。表示を確認してから、必要な場合だけもう一度お試しください。")}
            </Text>
          </Banner>
        )}
        {alerts.map((alert) => {
          const destination = reorderDestination(alert);
          return (
            <Box
              key={alert.id}
              padding="300"
              background="bg-surface-secondary"
              borderRadius="200"
            >
              {/* wrap を止めない＝ボタンが2つになったので、狭い画面では折り返させる。 */}
              <InlineStack align="space-between" blockAlign="center" gap="300">
                <BlockStack gap="100">
                  <InlineStack gap="200" blockAlign="center">
                    <Badge tone={alert.kind === "out_of_stock" ? "critical" : "warning"}>
                      {t(KIND_LABEL[alert.kind])}
                    </Badge>
                    <Text as="span" variant="bodyMd" fontWeight="semibold">
                      {alert.sku ?? `variant ${alert.shopify_variant_id}`}
                    </Text>
                  </InlineStack>
                  <Text as="span" variant="bodySm" tone="subdued">
                    {alert.title ?? "—"} ／{" "}
                    {t("現在 {current} 個（しきい値 {threshold}）", {
                      current: alert.current_value,
                      threshold: alert.threshold,
                    })}
                  </Text>
                </BlockStack>
                <InlineStack gap="200" blockAlign="center">
                  <Button variant="primary" onClick={() => navigate(destination.path)}>
                    {destination.label}
                  </Button>
                  <Button
                    onClick={() => mutation.mutate(alert.id)}
                    loading={mutation.isPending && mutation.variables === alert.id}
                  >
                    {t("対応済みにする")}
                  </Button>
                </InlineStack>
              </InlineStack>
            </Box>
          );
        })}
      </BlockStack>
    </Card>
  );
}
